<template>
    <div v-if="hasSearchConfiguration">
        <AisInstantSearchSsr @error="(error: Error) => console.error(error)">
            <CategorySearchRefinementKeepers :attributes="visibleFilterPanels.map(filter => filter.filter_identifier)" />

            <!-- Hidden: lets the uiState's sortBy, derived from the Nieuw refinement, switch to the Nieuw replica. -->
            <AisSortBy v-if="nieuwSortByItems" class="hidden" :items="nieuwSortByItems">
                <template #default>
                    <span />
                </template>
            </AisSortBy>

            <!-- Client-only: SSR has no URL routing, so its root class would always read "noRefinement". -->
            <AisCurrentRefinements v-if="isMounted">
                <template #default="{ items }">
                    <div v-if="publishCurrentRefinements(items)" class="hidden" />
                </template>
            </AisCurrentRefinements>

            <div class="lg:flex lg:gap-7">
                <CategorySearchInlineFilters
                    :category-search="categorySearch"
                    :lister-page-description="listerPageDescription"
                    :selected-filter-counts="selectedFilterCounts"
                />

                <CategorySearchResultsPanel
                    class="lg:flex-1"
                    :category-search="categorySearch"
                    :initial-results="initialResults"
                    :selected-filter-count="selectedFilterCount"
                />
            </div>

            <div v-if="categorySearch.description.value" class="mt-10 border-t border-zinc-200 pt-8">
                <div
                    class="prose max-w-none text-sm leading-7 text-zinc-600 prose-table:block prose-table:overflow-x-auto"
                    v-safe-html="categorySearch.description.value"
                />
            </div>

            <div v-if="categorySearch.faqs.value?.length" class="mt-10">
                <div class="prose max-w-none text-sm leading-7 text-zinc-600">
                    <h2>{{ $t('pages.category.faqs.title') }}</h2>

                    <template v-for="(faq, index) in categorySearch.faqs.value" :key="index">
                        <h3>{{ faq.question }}</h3>
                        <p>{{ faq.answer }}</p>
                    </template>
                </div>
            </div>

            <CategorySearchMobileFilters
                v-if="hasOpenedMobileFilters"
                :open="categorySearch.isMobileFiltersOpen.value"
                :lister-page-description="listerPageDescription"
                :filter-panels="activeFilterPanels"
                :viewport="activeFilterViewport"
                :selected-filter-counts="selectedFilterCounts"
                :collection-filter-attribute="categorySearch.collectionFilterAttribute"
                :default-facet-value-limit="categorySearch.defaultFacetValueLimit"
                :default-show-more-limit="categorySearch.defaultShowMoreLimit"
                @close="categorySearch.closeMobileFilters"
                @show-results="categorySearch.handleMobileFilterApplied"
            />
        </AisInstantSearchSsr>
    </div>
</template>

<script setup lang="ts">
import type { CategoryCurrentRefinementGroup } from '@/components/category-search/CategoryCurrentRefinements';
import { useCategorySearch } from '@/composables/category/useCategorySearch';
import { useCategorySearchSelectedFiltersSync } from '@/composables/category/useCategorySearchSelectedFiltersSync';
import { useHydratedInstantSearch } from '@/composables/category/useHydratedInstantSearch';
import { provideScoredFacetCounts } from '@/composables/category/useScoredFacetCounts';
import CategorySearchInlineFilters from '@/pages/category/partials/CategorySearchInlineFilters.vue';
import CategorySearchRefinementKeepers from '@/pages/category/partials/CategorySearchRefinementKeepers.vue';
import CategorySearchResultsPanel from '@/pages/category/partials/CategorySearchResultsPanel.vue';
import { categoryPageStore, type CategoryPageProps } from '@/stores/category/CategoryPageStore';
import type { CategoryInitialResultObject } from '@/types/generated';
import type { UiState } from 'instantsearch.js/es/types';
import { withNieuwSortBy } from '@/utils/nieuwReplica';
import { countCurrentRefinements, countResolvedRefinements } from '@/utils/refinementCount';
import { usePage } from '@inertiajs/vue3';
import _ from 'lodash';
import { computed, defineAsyncComponent, onMounted, provide, ref, watch } from 'vue';
// @ts-expect-error TS7016 Vue instantsearch doesn't have typehints
import { AisCurrentRefinements, AisInstantSearchSsr, AisSortBy } from 'vue-instantsearch/vue3/es';

const props = defineProps<{
    initialResults: CategoryInitialResultObject[];
    listerPageDescription: string | undefined;
}>();

const page = usePage<CategoryPageProps>();
const pageStore = categoryPageStore();
const categorySearch = useCategorySearch();
const { syncSelectedFilters } = useCategorySearchSelectedFiltersSync();

// Seeded from the server's resolved filters, so counts render with the page instead of after InstantSearch reports.
const selectedFilterCounts = ref<Record<string, number>>(
    countResolvedRefinements(pageStore.initialRefinements, categorySearch.categoryFilters.value),
);
const selectedFilterCount = computed((): number => _.sum(_.values(selectedFilterCounts.value)));

const isMounted = ref<boolean>(false);
onMounted((): void => {
    isMounted.value = true;
});

// Only reached once mounted (the caller sits under `v-if="isMounted"`), so SSR/hydration counts stay untouched.
function publishCurrentRefinements(items: CategoryCurrentRefinementGroup[]): true {
    const counts = countCurrentRefinements(items, categorySearch.categoryFilters.value);

    if (!_.isEqual(counts, selectedFilterCounts.value)) {
        selectedFilterCounts.value = counts;
    }

    return syncSelectedFilters(items);
}

// Closed at load; keeps the headlessui Dialog + drawer content out of the initial bundle
// until the shopper actually opens the mobile filters.
const CategorySearchMobileFilters = defineAsyncComponent(() => import('@/pages/category/partials/CategorySearchMobileFilters.vue'));
const hasOpenedMobileFilters = ref(false);

watch(
    () => categorySearch.isMobileFiltersOpen.value,
    (open: boolean): void => {
        if (!open) {
            return;
        }

        hasOpenedMobileFilters.value = true;
    },
);

// Created once in setup: InstantSearch cannot swap its client or index after construction.
const hasSearchConfiguration = !!categorySearch.searchClient.value && !!categorySearch.productIndex.value;
const nieuwReplica = categorySearch.productStore.nieuwReplica;
const nieuwSortByItems = nieuwReplica
    ? [
          { value: categorySearch.productIndex.value!, label: categorySearch.productIndex.value! },
          { value: nieuwReplica.index, label: nieuwReplica.index },
      ]
    : undefined;

// Refining or clearing Nieuw switches the index in the same search.
const onStateChange = ({ uiState, setUiState }: { uiState: UiState; setUiState: (uiState: UiState) => void }): void =>
    setUiState(withNieuwSortBy(uiState, categorySearch.productIndex.value!, nieuwReplica));

if (hasSearchConfiguration) {
    const scoring = page.props.filter_scoring;
    const scored = _.isEmpty(scoring) ? undefined : provideScoredFacetCounts(scoring!, page.props.initial_scored_counts);

    provide(
        '$_ais_ssrInstantSearchInstance',
        useHydratedInstantSearch(
            {
                searchClient: categorySearch.searchClient.value!,
                indexName: categorySearch.productIndex.value!,
                insights: categorySearch.productStore.insightsEnabled,
                insightsClient: categorySearch.insightsClient,
                routing: categorySearch.routing,
                future: categorySearch.instantSearchFuture,
                ...(nieuwReplica ? { onStateChange } : {}),
            },
            // Unresolvable URL filters: the server state lacks them, so hydrating it would mismatch the client's routed search.
            pageStore.initialRefinements === null ? undefined : (page.props.initial_search_state ?? undefined),
            scored,
            _.compact([nieuwReplica?.index]),
        ),
    );
}
const visibleFilterPanels = computed(() => categorySearch.visibleFilterPanels.value);
const activeFilterPanels = computed(() => categorySearch.activeFilterPanels.value);
const activeFilterViewport = computed(() => categorySearch.activeFilterViewport.value);
</script>
